/**
 * scripts-components-header.js
 * -----------------------------------------------------------------------
 * `<am-header>`: toggles an `is-scrolled` class used to fade in the soft
 * shadow once the page has scrolled, so the header stays flush with the
 * page at the very top.
 *
 * `<am-disclosure>`: small accessible dropdown/accordion primitive reused
 * by the desktop mega menu, simple nav dropdowns and the mobile nav
 * drawer accordion. Opens on trigger click, closes on outside click,
 * Escape, or when another disclosure opens (single-open accordion
 * behavior for the main nav).
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var dom = window.AniMarket && window.AniMarket.dom;

  class AmHeaderElement extends HTMLElement {
    connectedCallback() {
      var handler = this._updateScrolledState.bind(this);
      this._onScroll = dom ? dom.throttle(handler, 100) : handler;
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._updateScrolledState();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
    }

    _updateScrolledState() {
      this.classList.toggle('is-scrolled', window.scrollY > 4);
    }
  }

  class AmDisclosureElement extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-disclosure-trigger]');
      this.panel = this.querySelector('[data-disclosure-panel]');
      if (!this.trigger || !this.panel) return;

      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.addEventListener('click', this._onTriggerClick.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));
      this._onDocumentClick = this._onDocumentClick.bind(this);
      document.addEventListener('click', this._onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
    }

    _onTriggerClick(event) {
      event.stopPropagation();
      var willOpen = !this.hasAttribute('open');
      closeAllDisclosures(this);
      this.toggleAttribute('open', willOpen);
      this.trigger.setAttribute('aria-expanded', String(willOpen));
    }

    _onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeDisclosure();
    }

    _onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeDisclosure();
      this.trigger.focus();
    }

    closeDisclosure() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  function closeAllDisclosures(except) {
    document.querySelectorAll('am-disclosure[open]').forEach(function (node) {
      if (node !== except && typeof node.closeDisclosure === 'function') {
        node.closeDisclosure();
      }
    });
  }

  // Mega menu panels are rendered once into a hidden pool (see
  // sections/header.liquid) because `content_for 'block'` requires
  // literal id/type arguments and can't be looked up per nav link.
  // Move each panel into its matching `<am-disclosure>` slot *before* the
  // custom elements below are defined, so `connectedCallback` (which runs
  // synchronously on `customElements.define`) finds the panel already in
  // place.
  function moveMegaMenuPanelsIntoSlots() {
    var pool = document.querySelector('[data-mega-menu-pool]');
    if (!pool) return;
    document.querySelectorAll('[data-mega-menu-slot]').forEach(function (slot) {
      var triggerLabel = slot.getAttribute('data-mega-menu-slot');
      var panel = pool.querySelector('[data-trigger-label="' + CSS.escape(triggerLabel) + '"]');
      if (panel) slot.replaceWith(panel);
    });
  }

  moveMegaMenuPanelsIntoSlots();

  if (!customElements.get('am-header')) customElements.define('am-header', AmHeaderElement);
  if (!customElements.get('am-disclosure')) customElements.define('am-disclosure', AmDisclosureElement);
})();
